import type { NxHue } from '../lib/hue';

/** Intent colour of a mark: for a series or share that MEANS something (overdue = `danger`). */
export type NxChartTone = 'brand' | 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/** Colour of a series: an intent tone, a category hue, or (unset) its slot in the chart palette. */
export type NxChartColor = NxChartTone | NxHue;

/** Number of categorical slots (`--nxt-chart-1` … `-8`). A ninth series repeats the first. */
export const NX_CHART_SLOTS = 8;

const TONES: readonly string[] = ['brand', 'neutral', 'info', 'success', 'warning', 'danger'];

/**
 * CSS colour of the series at `index`: its tone or hue when given, else the
 * palette slot of its position. The palette ORDER keeps neighbours apart for
 * colour-blind readers — pass `color` wherever a series must keep its colour
 * while others come and go (colour follows the entity, never its rank).
 */
export function chartColor(color: NxChartColor | undefined, index: number): string {
  if (color) return TONES.includes(color) ? `var(--nxt-${color}-bg)` : `var(--nxt-hue-${color})`;
  const slot = ((index % NX_CHART_SLOTS) + NX_CHART_SLOTS) % NX_CHART_SLOTS;
  return `var(--nxt-chart-${slot + 1})`;
}

export type NxChartKey = string | number;

/** One series of a category chart (bar, horizontal bar, line). */
export interface NxChartSeries {
  key: NxChartKey;
  label: string;
  /** One value per category; `null`/`undefined` = no value there (a gap, no bar). */
  values: readonly (number | null | undefined)[];
  color?: NxChartColor;
}

/** A value that can be drawn: finite, else `null`. */
export function chartValue(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export interface NxStackSegment {
  /** Position of the series in the input list. */
  seriesIndex: number;
  key: NxChartKey;
  label: string;
  value: number;
  /** Start and end of the segment in value space (`from` is nearer to 0). */
  from: number;
  to: number;
  color: string;
}

export interface NxStackColumn {
  index: number;
  label: string;
  /** Positive segments stack up from 0, negative ones down from 0, in series order. */
  segments: NxStackSegment[];
  /** Sum of all drawn values (positives + negatives). */
  net: number;
  /** Whether the column has any drawn value. */
  hasValue: boolean;
}

/**
 * Stacks the series per category on ONE value scale: positives up from zero,
 * negatives down from zero. `min`/`max` span every stack (and 0).
 */
export function stackColumns(
  categories: readonly string[],
  series: readonly NxChartSeries[]
): { columns: NxStackColumn[]; min: number; max: number } {
  let min = 0;
  let max = 0;
  const columns = categories.map((label, index) => {
    let up = 0;
    let down = 0;
    let net = 0;
    let hasValue = false;
    const segments: NxStackSegment[] = [];
    series.forEach((s, seriesIndex) => {
      const value = chartValue(s.values[index]);
      if (value === null) return;
      hasValue = true;
      net += value;
      const base = value < 0 ? down : up;
      const to = base + value;
      if (value < 0) down = to;
      else up = to;
      segments.push({
        seriesIndex,
        key: s.key,
        label: s.label,
        value,
        from: base,
        to,
        color: chartColor(s.color, seriesIndex),
      });
    });
    if (up > max) max = up;
    if (down < min) min = down;
    return { index, label, segments, net, hasValue };
  });
  return { columns, min, max };
}

/** Smallest and largest drawn value over all series (0 included). */
export function seriesExtent(series: readonly NxChartSeries[]): { min: number; max: number } {
  let min = 0;
  let max = 0;
  for (const s of series) {
    for (const raw of s.values) {
      const v = chartValue(raw);
      if (v === null) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  return { min, max };
}
